import { v4 as uuidv4 } from "uuid"
import { HttpRequest } from "../utils"

const httpRequest = new HttpRequest()

export interface IRouteStopDnSummary {
  refCode: string | null
  receiverBusinessName: string | null
  receiverBranchName: string | null
  totalWeightKg: number
  sizeCode: string | null
  deliveryNoteStatus: string | null
}

export interface IRouteStop {
  id: string
  routeId: string
  deliveryNoteId: string
  stopNumber: number
  status: string
  address: string | null
  latitude: number | null
  longitude: number | null
  plannedArrivalTime: string | null
  actualArrivalTime: string | null
  actualDepartureTime: string | null
  distanceFromPrevStopKm: string | null
  // Hydrated by /ord/delivery-route/list when stops are embedded. Lets the
  // Routes-tab card render rich rows (DN code, customer, weight, size)
  // without a separate fetch per stop.
  dn?: IRouteStopDnSummary | null
}

export interface IRouteDriver {
  id: string
  lastName: string | null
  firstName: string | null
  phone: string | null
}

export interface IRouteVehicle {
  id: string
  plateNumber: string
  vehicleClass: string
  make: string | null
  model: string | null
}

export interface IRouteWarehouse {
  id: string
  name: string | null
  latitude: number | null
  longitude: number | null
}

export interface IRoutePullSheetStaff {
  id: string
  lastName: string | null
  firstName: string | null
  email: string | null
  phone: string | null
  avatar: string | null
}

export interface IDeliveryRoute {
  id: string
  businessId: string
  routeNumber: string
  name: string | null
  warehouseId: string | null
  driverId: string | null
  vehicleId: string | null
  pullSheetStaffId: string | null
  pullSheetStaffIds: string[]
  status: string
  plannedDate: string
  plannedStartTime: string | null
  plannedEndTime: string | null
  totalStops: number
  completedStops: number
  failedStops: number
  skippedStops: number
  totalDistanceKm: string | null
  plannedDurationMinutes: number | null
  actualDurationMinutes: number | null
  isOptimized: boolean
  optimizationScore: string | null
  startedAt: string | null
  completedAt: string | null
  createdAt?: string
  updatedAt?: string
  stops?: IRouteStop[]
  // Hydrated by /ord/delivery-route/list when the row has a driverId /
  // vehicleId. Null when no driver/vehicle is assigned yet.
  driver?: IRouteDriver | null
  vehicle?: IRouteVehicle | null
  // Hydrated by GET /ord/delivery-route/:id (single user + array of users
  // matching pullSheetStaffId / pullSheetStaffIds).
  pullSheetStaff?: IRoutePullSheetStaff | null
  pullSheetStaffs?: IRoutePullSheetStaff[]
  // Distinct distribution-area (бүс / чиглэл) labels derived from the route's
  // stops via de_network. Hydrated by GET /ord/delivery-route/:id.
  zones?: string[]
  // GET /ord/delivery-route/:id only. `notes` is the free-text note typed in the wizard (null when
  // none or when the DB has no notes column yet); `warehouse` carries the depot coordinates that
  // start/end the optimization (null when the route has no warehouse).
  notes?: string | null
  warehouse?: IRouteWarehouse | null
}

export interface IRouteListResponse {
  rows: IDeliveryRoute[]
  count: number
}

export interface ICreateRouteInput {
  plannedDate: string
  name?: string | null
  warehouseId?: string | null
  driverId?: string | null
  vehicleId?: string | null
  /** ISO datetime (planned date + target departure time); BE delivery-route/create.ts accepts null / "". */
  plannedStartTime?: string | null
  /** Free-text note, at most 2000 characters. */
  notes?: string | null
  stops: Array<{
    deliveryNoteId: string
    address?: string | null
    latitude?: number | null
    longitude?: number | null
  }>
}

// §26 — create/start/complete wrap in `withIdempotency` on the BE. A fresh
// uuid v4 per call lets a network retry replay safely (non-strict: a missing
// key just skips dedup, never 400s).
const idem = (): Record<string, string> => ({ "Idempotency-Key": uuidv4() })

export const create = async (data: ICreateRouteInput): Promise<IDeliveryRoute> => {
  return httpRequest.post("/ord/delivery-route", data as any, idem())
}

export const list = async (data: {
  filter?: {
    status?: string
    driverId?: string
    plannedDate?: string
    /** When set, only routes containing a stop with this DN id are returned.
     *  Backend embeds stops on each route in this mode. */
    deliveryNoteId?: string
  }
  offset?: { limit: number; skip: number }
}): Promise<IRouteListResponse> => {
  return httpRequest.post("/ord/delivery-route/list", data as any)
}

export const get = async (id: string): Promise<IDeliveryRoute> => {
  return httpRequest.get(`/ord/delivery-route/${id}`)
}

export const start = async (id: string): Promise<IDeliveryRoute> => {
  return httpRequest.post(`/ord/delivery-route/${id}/start`, {} as any, idem())
}

export const complete = async (id: string): Promise<IDeliveryRoute> => {
  return httpRequest.post(`/ord/delivery-route/${id}/complete`, {} as any, idem())
}

// Marks the loading phase done (allowed from PLANNED | LOADING -> LOADING).
// NOT wrapped in withIdempotency on the BE (unlike start/complete), so no
// Idempotency-Key here.
export const loadingComplete = async (id: string): Promise<IDeliveryRoute> => {
  return httpRequest.post(`/ord/delivery-route/${id}/loading-complete`, {} as any)
}

export const reorderStops = async (id: string, body: { orderedStopIds: string[] }): Promise<IDeliveryRoute> => {
  return httpRequest.post(`/ord/delivery-route/${id}/reorder-stops`, body as any)
}

export const addStops = async (
  id: string,
  body: {
    stops: Array<{
      deliveryNoteId: string
      address?: string | null
      latitude?: number | null
      longitude?: number | null
    }>
  },
): Promise<IDeliveryRoute> => {
  return httpRequest.post(`/ord/delivery-route/${id}/add-stops`, body as any)
}

export interface IPullSheetStaffResponse {
  id: string
  pullSheetStaffId: string | null
  pullSheets: string[]
  skippedReasons: string[]
}

export const pullSheetStaff = async (
  id: string,
  // Backend accepts either a single id or an array (`.or(...)`). Pass
  // `pullSheetStaffIds` to assign multiple loading staff to one route.
  body: { pullSheetStaffId: string | null } | { pullSheetStaffIds: string[] },
): Promise<IPullSheetStaffResponse> => {
  return httpRequest.post(`/ord/delivery-route/${id}/pull-sheet-staff`, body as any)
}

export interface IAssignmentResponse {
  id: string
  driverId: string | null
  vehicleId: string | null
  status: string
}

export const updateAssignment = async (
  id: string,
  body: { driverId?: string | null; vehicleId?: string | null },
): Promise<IAssignmentResponse> => {
  return httpRequest.put(`/ord/delivery-route/${id}/assignment`, body as any)
}

export const optimize = async (id: string, body: {
  startFrom: { lat: number; lng: number }
  returnTo?: { lat: number; lng: number } | null
  averageSpeedKmh?: number
}): Promise<IDeliveryRoute> => {
  return httpRequest.post(`/ord/delivery-route/${id}/optimize`, body as any)
}

// §2.5 Tab 4 — full driver GPS trail for the route, recordedAt ASC, for the
// playback scrubber. Empty when the trail aged out of retention (default 30d)
// or the driver app never pushed GPS. Read-only.
export interface ITrailPoint {
  deliveryNoteId: string
  driverUserId: string
  latitude: number
  longitude: number
  headingDeg: number | null
  speedKmh: number | null
  recordedAt: string
}

export const getLocationHistory = async (
  id: string,
): Promise<{ rows: ITrailPoint[]; count: number }> => {
  return httpRequest.get(`/ord/delivery-route/${id}/location-history`)
}
